/**
 * "Before leads reach you": the steps a contractor with a page from us has to
 * take that nothing else on the page path asks for (owner decision 2026-10-20:
 * a checklist with links, no automatic config changes).
 *
 *   - Where leads go: a new site's published routing is `finderly_app`, so the
 *     page's quote form stays off until the person picks their own team and
 *     publishes. The server says so in the profile's `quote_form.reason`
 *     (model.ts `quoteFormBlock`).
 *   - Contractors: lead emails go to the roster. A solo contractor has to add
 *     themselves.
 *   - The Contractor Fee Schedule, once FinderlyFix has switched billing on:
 *     leads cannot be accepted before it is accepted.
 *   - The page itself, while the completeness checklist has items.
 *
 * Each item renders only while it is open, so a page that is ready shows one
 * line. Reads are the ones the portal already makes elsewhere; a read this
 * role may not make (billing, the roster) is skipped rather than shown as an
 * error.
 */
import type { HostedPageProfileResponse } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { useQuery } from '../ui/hooks';
import { quoteFormBlock } from './model';

export type ReadinessFacts = Pick<HostedPageProfileResponse, 'completeness' | 'quote_form'>;

export function LeadReadiness({ slug, facts }: { slug: string; facts?: ReadinessFacts | null }) {
  const { api, role } = useSession();
  const profile = useQuery(() => api.getHostedPageProfile(slug), [slug], { enabled: !facts && can(role, 'hostedPageProfile') });
  const roster = useQuery(() => api.listVendors({ active: 'true', limit: 1 }), [], { enabled: can(role, 'roster') });
  const billing = useQuery(() => api.getBilling(), [], { enabled: can(role, 'billing') });
  const known = facts ?? profile.data ?? null;

  const items: Array<{ key: string; text: string; to: string; label: string }> = [];
  const block = known ? quoteFormBlock(known.quote_form.reason) : null;
  if (block?.fix?.to === '/setup/routing') {
    items.push({ key: 'routing', text: 'Choose your own team under Where leads go, then publish, so the quote form can send to you.', to: '/setup/routing', label: 'Where leads go' });
  }
  if (roster.data && roster.data.items.length === 0) {
    items.push({ key: 'roster', text: 'Add yourself under Contractors so the lead emails from your page reach you.', to: '/contractors', label: 'Contractors' });
  }
  if (billing.data?.enabled && billing.data.fee_schedule?.required) {
    items.push({ key: 'fees', text: 'Accept the Contractor Fee Schedule: leads can’t be accepted before it is.', to: '/billing#fee-schedule', label: 'Billing' });
  }
  if (known && known.completeness.missing.length) {
    items.push({ key: 'page', text: `Finish your page (${known.completeness.score}% complete): homeowners call the page that answers their questions.`, to: `/pages/${encodeURIComponent(slug)}`, label: 'Your page' });
  }

  const loading = (!facts && profile.loading) || roster.loading || billing.loading;
  if (loading && !items.length) return null;
  return (
    <section className="panel panel-pad stack-sm" aria-labelledby="lead-readiness-title">
      <h2 id="lead-readiness-title" className="h-sub" style={{ margin: 0 }}>
        Before leads reach you
      </h2>
      {items.length ? (
        <ul className="stack-sm" style={{ margin: 0, paddingLeft: 20 }}>
          {items.map((item) => (
            <li key={item.key}>
              {item.text} <Link to={item.to}>{item.label}</Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Leads from your page reach you.
        </p>
      )}
    </section>
  );
}
